import { useMemo, useState } from 'react';
import { Badge, Dropdown, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { PerkGroup } from '../../domain/entities/catalog.entity';
import { usePerkGroups } from '../hooks/use-perks';
import {
  usePerkHolderCounts,
  perkGroupKey,
  perkGroupPerkIds,
} from '../hooks/use-perk-holders';
import { GRANT_POLICY_LABELS, PERK_TYPE_LABELS } from '../labels';
import { Pagination } from '@react-apps/design-system';
import { PerkHoldersModal } from './PerkHoldersModal';
import { RefreshButton } from '@/shared/RefreshButton';
import { ScopeBadgeList } from '@/shared/scopes/ScopeBadge';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

/** Scopes da capacidade: união dos scopes das suas formas de concessão. */
function groupScopeIds(group: PerkGroup): string[] {
  return [...new Set(group.grantForms.flatMap((f) => f.scopeIds))];
}

/**
 * Uma linha por **capacidade** `(tipo, key)`, com as formas de concessão
 * disponíveis — não por perk. Sem valores: duração e cota são de quem concede,
 * não do catálogo.
 */
export function PerksPanel() {
  const [page, setPage] = usePageQueryParam();
  const { data, isLoading, refetch, isFetching } = usePerkGroups(
    page,
    PER_PAGE,
  );

  // Qual linha está com o modal "ver usuários" aberto.
  const [holdersGroup, setHoldersGroup] = useState<PerkGroup | null>(null);

  // Um grupo por linha da página (key da capacidade + todos os seus perkId),
  // para a contagem em lote. `useMemo` para não refazer a query a cada render.
  const countGroups = useMemo(
    () =>
      (data?.items ?? []).map((group) => ({
        key: perkGroupKey(group),
        perkIds: perkGroupPerkIds(group),
      })),
    [data?.items],
  );
  const { data: counts } = usePerkHolderCounts(countGroups);

  const columns: ColumnDef<PerkGroup>[] = [
    {
      key: 'key',
      header: 'Capacidade',
      render: (row) => (
        <div className="space-y-0.5">
          <span className="text-(--color-text-primary) text-sm">
            {row.name ?? 'Sem definição'}
          </span>
          <div className="text-(--color-text-secondary) font-mono text-xs">
            {row.key}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Tipo',
      render: (row) => (
        <Badge variant="info">{PERK_TYPE_LABELS[row.type]}</Badge>
      ),
    },
    {
      key: 'grantForms',
      header: 'Formas de concessão',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          {row.grantForms.map((form) => (
            <div key={form.perkId} className="flex items-center gap-2">
              <span className="text-(--color-text-secondary) text-sm">
                {GRANT_POLICY_LABELS[form.grantPolicyKind]}
              </span>
              {(form.grantsPerkKeys.length > 0 ||
                form.requiresPerkKeys.length > 0) && (
                <Badge variant="default">
                  {form.grantsPerkKeys.length > 0
                    ? `concede ${form.grantsPerkKeys.length}`
                    : ''}
                  {form.grantsPerkKeys.length > 0 &&
                  form.requiresPerkKeys.length > 0
                    ? ' · '
                    : ''}
                  {form.requiresPerkKeys.length > 0
                    ? `exige ${form.requiresPerkKeys.length}`
                    : ''}
                </Badge>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'scopeIds',
      header: 'Scopes',
      render: (row) => {
        const scopeIds = groupScopeIds(row);
        return <ScopeBadgeList ids={scopeIds} />;
      },
    },
    {
      key: 'holders',
      header: 'Portadores',
      render: (row) => {
        const count = counts?.get(perkGroupKey(row));
        return (
          <span className="text-(--color-text-primary) text-sm tabular-nums">
            {count ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <Dropdown
          align="right"
          trigger={
            <button
              type="button"
              aria-label="Ações da capacidade"
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-primary) rounded-md px-2 py-1 text-sm"
            >
              ⋯
            </button>
          }
          items={[
            {
              id: 'holders',
              label: 'Ver usuários com esta capacidade',
              onClick: () => setHoldersGroup(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-(--color-text-secondary) text-sm">
          Capacidades concedíveis (roles e features) e as formas sob as quais
          cada uma pode ser concedida.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => `${row.type}:${row.key}`}
        loading={isLoading}
        emptyMessage="Nenhum perk encontrado."
        striped
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="capacidade"
        onPageChange={setPage}
      />

      <PerkHoldersModal
        group={holdersGroup}
        onClose={() => setHoldersGroup(null)}
      />
    </div>
  );
}
